import { Box, Typography, Modal, Divider, IconButton, CircularProgress } from "@mui/material";

import { useState, useEffect } from "react";
import {
  AgentService,
  BlockchainAnalysisResponse,
  CorrespondenceResponse,
  ProductRequestType,
  TrackMasterResponse,
  WebIntelligenceResponse,
} from "../../../services/agent.service";
import { GridCloseIcon } from "@mui/x-data-grid";
import { ProductRequestInfoAndActionsModalProps } from "./types";
import BlockchainAnalysisDetails from "./blockchain-analysis";
import CorrespondenceDetails from "./correspondence";
import WebIntelligenceDetails from "./web-intelligence";
import TrackMasterDetails from "./track-master";

const getProductTitle = (type: ProductRequestType): string => {
  switch (type) {
    case "Web Intelligence":
      return "Web Intelligence 🌐";
    case "Blockchain Analysis":
      return "Blockchain Analysis ⛓️";
    case "Correspondence":
      return "Correspondence ✉️";
    case "Track Master":
      return "Track Master 👁️";
  }
};

const ProductRequestInfoAndActionsModal: React.FC<ProductRequestInfoAndActionsModalProps> = ({ isOpen, onClose, productMetaData, clientName, clientEmail, ownerEmail }) => {
  const [product, setProduct] = useState<WebIntelligenceResponse | BlockchainAnalysisResponse | CorrespondenceResponse | TrackMasterResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setIsLoading(true);
        const response = await AgentService.getProductFullData({
          clientCrmProfileId: productMetaData.crmProfileId,
          productId: productMetaData.productId,
          productRequestType: productMetaData.productRequestType,
        });
        setProduct(response);
      } catch (error) {
        setError("Failed to load product details");
        console.error("Error fetching product:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProduct();
  }, [productMetaData.productId]);

  // Prevent background scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = window.getComputedStyle(document.body).position;

      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";

      return () => {
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.width = "";
      };
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      disableScrollLock={false}
      hideBackdrop={false}
      sx={{
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
          pointerEvents: "none",
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(8px)", // blur מסביב למודל
          overflow: "hidden",
          zIndex: 1300,
        }}
      >
        <Box
          sx={{
            overflowY: "auto",
            p: 4,
            width: "600px",
            maxWidth: "95vw",
            height: "800px", // גובה קבוע
            maxHeight: "95vh",
            background: "white",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            borderRadius: 4,
            position: "relative",
            pointerEvents: "auto",
            outline: "none",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <GridCloseIcon />
          </IconButton>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: 2,
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="h6">Fetching product details...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <Typography variant="h6" color="error">
                {error}
              </Typography>
            </Box>
          ) : product ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography sx={{ fontSize: "35px" }} variant="h6" textAlign={"center"}>
                {getProductTitle(productMetaData.productRequestType)}
              </Typography>
              <Divider />
              {productMetaData.productRequestType === "Blockchain Analysis" ? (
                <BlockchainAnalysisDetails
                  onProductUpdate={setProduct}
                  productMetaData={productMetaData}
                  productInfo={product as BlockchainAnalysisResponse}
                />
              ) : productMetaData.productRequestType === "Correspondence" ? (
                <CorrespondenceDetails
                  onProductUpdate={setProduct}
                  productMetaData={productMetaData}
                  productInfo={product as CorrespondenceResponse}
                />
              ) : productMetaData.productRequestType === "Web Intelligence" ? (
                <WebIntelligenceDetails
                  onUpdateProduct={setProduct}
                  productMetaData={productMetaData}
                  productInfo={product as WebIntelligenceResponse}
                />
              ) : productMetaData.productRequestType === "Track Master" ? (
                <TrackMasterDetails
                  productMetaData={productMetaData}
                  productInfo={product as TrackMasterResponse}
                  clientName={clientName}
                  clientEmail={clientEmail}
                  ownerEmail={ownerEmail}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductRequestInfoAndActionsModal;
